import type { ExcalidrawElement } from "@excalidraw/element/types";
import type { AppState, BinaryFileData } from "@excalidraw/excalidraw/types";

export const WORKSPACE_BACKEND = (
  import.meta.env.VITE_APP_WORKSPACE_BACKEND || ""
).replace(/\/$/, "");

export const isWorkspaceEnabled = () => WORKSPACE_BACKEND.length > 0;

export interface WorkspaceUser {
  id: string;
  email: string;
  displayName: string;
}

export interface SceneMeta {
  id: string;
  name: string;
  folderId: string | null;
  createdAt: number;
  updatedAt: number;
}

export interface FullScene extends SceneMeta {
  elements: readonly ExcalidrawElement[];
  appState: Partial<AppState>;
}

export interface WorkspaceFolder {
  id: string;
  name: string;
  parentId: string | null;
  createdAt: number;
}

export type ShareRole = "view" | "edit";

export interface Share {
  token: string;
  role: ShareRole;
  url: string;
  createdAt?: number;
}

export interface WorkspaceComment {
  id: string;
  sceneId: string;
  author: string;
  x: number;
  y: number;
  text: string;
  resolved: boolean;
  createdAt: number;
}

export interface WorkspaceFile {
  id: string;
  mimeType: string;
  dataURL: string;
  created: number;
}

export class WorkspaceApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
    this.name = "WorkspaceApiError";
  }
}

const request = async <T>(
  path: string,
  options: RequestInit = {},
): Promise<T> => {
  const res = await fetch(`${WORKSPACE_BACKEND}${path}`, {
    credentials: "include",
    headers:
      options.body != null
        ? { "Content-Type": "application/json", ...(options.headers || {}) }
        : options.headers,
    ...options,
  });

  if (!res.ok) {
    let message = res.statusText;
    try {
      const body = await res.json();
      if (body?.error) {
        message = body.error;
      }
    } catch {
      // ignore non-JSON error bodies
    }
    throw new WorkspaceApiError(res.status, message);
  }

  if (res.status === 204) {
    return undefined as T;
  }
  return (await res.json()) as T;
};

const body = (data: unknown): RequestInit => ({ body: JSON.stringify(data) });

export const workspaceApi = {
  // ---- auth ----
  me: () => request<{ user: WorkspaceUser | null }>("/auth/me"),
  register: (email: string, password: string, displayName?: string) =>
    request<{ user: WorkspaceUser }>("/auth/register", {
      method: "POST",
      ...body({ email, password, displayName }),
    }),
  login: (email: string, password: string) =>
    request<{ user: WorkspaceUser }>("/auth/login", {
      method: "POST",
      ...body({ email, password }),
    }),
  logout: () => request<{ ok: true }>("/auth/logout", { method: "POST" }),

  // ---- scenes ----
  listScenes: () => request<{ scenes: SceneMeta[] }>("/scenes"),
  createScene: (payload: {
    name: string;
    folderId?: string | null;
    elements?: readonly ExcalidrawElement[];
    appState?: Partial<AppState>;
  }) =>
    request<{ scene: SceneMeta }>("/scenes", {
      method: "POST",
      ...body(payload),
    }),
  getScene: (id: string) => request<{ scene: FullScene }>(`/scenes/${id}`),
  updateScene: (
    id: string,
    patch: {
      name?: string;
      folderId?: string | null;
      elements?: readonly ExcalidrawElement[];
      appState?: Partial<AppState>;
    },
  ) =>
    request<{ scene: SceneMeta }>(`/scenes/${id}`, {
      method: "PUT",
      ...body(patch),
    }),
  deleteScene: (id: string) =>
    request<{ ok: true }>(`/scenes/${id}`, { method: "DELETE" }),
  uploadFiles: (id: string, files: BinaryFileData[]) =>
    request<{ ok: true }>(`/scenes/${id}/files`, {
      method: "POST",
      ...body({ files }),
    }),
  getFiles: (id: string, ids: string[]) =>
    request<{ files: WorkspaceFile[] }>(
      `/scenes/${id}/files${ids.length ? `?ids=${ids.join(",")}` : ""}`,
    ),

  // ---- folders ----
  listFolders: () => request<{ folders: WorkspaceFolder[] }>("/folders"),
  createFolder: (name: string, parentId?: string | null) =>
    request<{ folder: WorkspaceFolder }>("/folders", {
      method: "POST",
      ...body({ name, parentId }),
    }),
  updateFolder: (
    id: string,
    patch: { name?: string; parentId?: string | null },
  ) =>
    request<{ folder: WorkspaceFolder }>(`/folders/${id}`, {
      method: "PUT",
      ...body(patch),
    }),
  deleteFolder: (id: string) =>
    request<{ ok: true }>(`/folders/${id}`, { method: "DELETE" }),

  // ---- sharing ----
  createShare: (sceneId: string, role: ShareRole) =>
    request<{ share: Share }>(`/scenes/${sceneId}/shares`, {
      method: "POST",
      ...body({ role }),
    }),
  listShares: (sceneId: string) =>
    request<{ shares: Share[] }>(`/scenes/${sceneId}/shares`),
  deleteShare: (token: string) =>
    request<{ ok: true }>(`/shares/${token}`, { method: "DELETE" }),
  // public (no auth) endpoints:
  getShare: (token: string) =>
    request<{
      role: ShareRole;
      scene: {
        id: string;
        name: string;
        elements: readonly ExcalidrawElement[];
        appState: Partial<AppState>;
        updatedAt: number;
      };
    }>(`/shares/${token}`),
  getShareFiles: (token: string, ids: string[]) =>
    request<{ files: WorkspaceFile[] }>(
      `/shares/${token}/files${ids.length ? `?ids=${ids.join(",")}` : ""}`,
    ),
  updateShare: (
    token: string,
    patch: {
      name?: string;
      elements?: readonly ExcalidrawElement[];
      appState?: Partial<AppState>;
    },
  ) =>
    request<{ ok: true }>(`/shares/${token}`, {
      method: "PUT",
      ...body(patch),
    }),
  uploadShareFiles: (token: string, files: BinaryFileData[]) =>
    request<{ ok: true }>(`/shares/${token}/files`, {
      method: "POST",
      ...body({ files }),
    }),

  // ---- comments ----
  listComments: (sceneId: string) =>
    request<{ comments: WorkspaceComment[] }>(`/scenes/${sceneId}/comments`),
  createComment: (
    sceneId: string,
    payload: { x: number; y: number; text: string },
  ) =>
    request<{ comment: WorkspaceComment }>(`/scenes/${sceneId}/comments`, {
      method: "POST",
      ...body(payload),
    }),
  updateComment: (id: string, patch: { text?: string; resolved?: boolean }) =>
    request<{ comment: WorkspaceComment }>(`/comments/${id}`, {
      method: "PUT",
      ...body(patch),
    }),
  deleteComment: (id: string) =>
    request<{ ok: true }>(`/comments/${id}`, { method: "DELETE" }),
};
